import { useState } from 'react';
import { ArrowLeftIcon, Download } from 'lucide-react';
import { useTranslate } from '../lib/i18n';
import type { AISurveyAnswerResponse } from '../services/backendService';
import { exportSurveyReportAsPdf } from '../utils/pdfExport';
import Button from './common/Button';
import ErrorToast from './ErrorToast';
import SessionPasswordCode from './SessionPasswordCode';

interface ExportSurveyProps {
  feedback: AISurveyAnswerResponse;
  sessionPassword?: { code: string; expiresAt: string | null } | null;
  createdAt: Date;
  onBack: () => void;
}

const ExportSurvey = ({ feedback, sessionPassword, createdAt, onBack }: ExportSurveyProps) => {
  const t = useTranslate();
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExportPdf = () => {
    try {
      setExportError(null);

      exportSurveyReportAsPdf({
        victimProfile: feedback.victim_profile || '',
        crimeOverview: feedback.crime_overview || '',
        immediateActions: feedback.immediate_actions || [],
        helpSources: feedback.help_sources || [],
        timestamp: createdAt,
        sectionTitles: {
          victimProfile: t('survey.victimProfile'),
          crimeOverview: t('survey.crimeOverview'),
          immediateActions: t('survey.doThisNow'),
          helpSources: t('survey.helpSources'),
          onlineHelp: t('survey.onlineHelp'),
          offlineHelp: t('survey.recommendedSourcesOfHelp'),
        },
        documentTitle: t('survey.exportTitle'),
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setExportError(t('survey.exportError'));
    }
  };

  return (
    <div className="min-h-full flex flex-col">
      <div className="flex-1 flex flex-col justify-center">
        <div className="max-w-lg mx-auto px-4 space-y-6">
          {/* Export PDF */}
          {sessionPassword && (
            <div className="border bg-card rounded-md p-4 space-y-3">
              <div className="text-muted-foreground text-sm">
                {t('survey.pdfDownload').toUpperCase()}
              </div>
              <Button
                variant="primary"
                onClick={handleExportPdf}
                className="w-full flex items-center justify-between"
              >
                {t('survey.exportButton')}
                <Download size={16} className="ml-2" />
              </Button>
              <p className="text-sm text-muted-foreground">
                {t('survey.downloadPdfDisclaimer.paragraphOne')}
              </p>
            </div>
          )}

          {/* Session Password */}
          {sessionPassword && (
            <div className="border bg-card rounded-md p-4 space-y-3">
              <SessionPasswordCode sessionPassword={sessionPassword} />
              <p className="text-sm text-muted-foreground">
                {t('survey.sessionPasswordInstructions')}
              </p>
              <ul className="text-sm text-muted-foreground">
                <li>- {t('survey.sessionPasswordHint1')}</li>
                <li>- {t('survey.sessionPasswordHint2')}</li>
                <li>- {t('survey.sessionPasswordHint3')}</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="bg-background border-t">
        <div className="py-4 max-w-lg mx-auto px-4">
          <Button variant="secondary" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeftIcon className="size-5" />
            {t('survey.backToReport')}
          </Button>
        </div>
      </div>

      <ErrorToast error={exportError} onClose={() => setExportError(null)} />
    </div>
  );
};

export default ExportSurvey;
